import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ShieldCheck, Star, ArrowRight } from "@phosphor-icons/react";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const lineUp = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const HERO_IMG =
  "https://images.pexels.com/photos/9462192/pexels-photo-9462192.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative pt-28 md:pt-40 pb-16 md:pb-24 overflow-hidden"
      data-testid="hero"
    >
      {/* decorative navy arc */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[var(--navy)]/5 blur-2xl" />
      <div className="pointer-events-none absolute top-20 right-0 h-64 w-64 rounded-full bg-[var(--gold)]/10 blur-2xl" />

      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--navy)]/15 bg-white px-4 py-1.5 mb-7"
          >
            <ShieldCheck weight="fill" size={16} className="text-[var(--gold)]" />
            <span className="label-caps text-[var(--navy)]">
              Pan India Service Provider · Trusted &amp; Verified
            </span>
          </motion.div>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className="font-display text-[var(--navy)] text-5xl md:text-7xl leading-[0.95] tracking-tighter"
          >
            <span className="line-mask">
              <motion.span variants={lineUp} className="block">
                Professional care
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span variants={lineUp} className="block">
                for your <span className="italic text-[var(--gold)]">home</span>
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span variants={lineUp} className="block">
                &amp; family.
              </motion.span>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-lg text-lg text-slate-600 leading-relaxed"
          >
            Quick Service. Spotless Results. Verified, background-checked and
            trained domestic help across India — from house maids and cooks to
            nannies, drivers, caretakers and more.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onBook}
              className="group inline-flex items-center gap-2 rounded-full bg-[var(--navy)] text-white font-bold px-7 py-4 hover:bg-[var(--navy-deep)] transition-colors"
              data-testid="hero-book-now"
            >
              Book a Service
              <ArrowRight
                weight="bold"
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
            <a
              href="#services"
              className="inline-flex items-center gap-2 rounded-full border border-[var(--navy)]/20 text-[var(--navy)] font-bold px-7 py-4 hover:bg-[var(--navy)]/5 transition-colors"
              data-testid="hero-view-services"
            >
              View Services
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="mt-10 flex items-center gap-6"
          >
            <div className="flex items-center gap-1.5 text-[var(--gold)]">
              {[...Array(3)].map((_, i) => (
                <Star key={i} weight="fill" size={18} />
              ))}
            </div>
            <p className="text-sm text-slate-500 font-medium">
              100% Customer Satisfaction · We Care Like Family
            </p>
          </motion.div>
        </div>

        {/* Image with clipped frame + parallax */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-3 rounded-[2rem] bg-[var(--gold)]/20 -rotate-2" />
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/5] bg-slate-100">
              <motion.img
                style={{ y, scale }}
                src={HERO_IMG}
                alt="Professional housekeeper"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/40 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl px-5 py-4 border border-slate-100"
            >
              <p className="font-display text-3xl font-bold text-[var(--navy)]">
                10+
              </p>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Services offered
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
